import React from "react"
import { Download, DollarSign } from "lucide-react"
import { ReportCard, ReportBadge } from "./ReportUI"
import { REPORT_COLORS } from "./reportTheme"
import { isBlurredField, blurAware, badgeAware, BlurredContentFade, PLACEHOLDER_SENTENCE } from "./BlurredField"
import { formatNumbersInText } from "./reportUtils"

// Matches the K/M formatting used for ai_downloads / ai_revenue on the
// authenticated game dashboard (gameDashboard.jsx: getDownloadsDisplay /
// getRevenueDisplay) so the two surfaces read consistently.
export const formatDownloads = downloads => {
  if (downloads == null || downloads === "") return "N/A"
  const num = Number(downloads)
  if (Number.isNaN(num)) return "N/A"
  if (num === 0) return "0"
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(2).replace(/\.?0+$/, "")}K`
  return num % 1 === 0 ? num.toString() : num.toFixed(0)
}

export const formatRevenue = revenue => {
  if (revenue == null) return "$0"
  const num = Number(revenue)
  if (!Number.isFinite(num) || num <= 0) return "$0"
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`
  return `$${num.toFixed(0)}`
}

const confidenceColor = score => (score >= 80 ? REPORT_COLORS.good : score >= 50 ? REPORT_COLORS.warning : REPORT_COLORS.critical)

// Whether ANY field of this estimation is individually locked — exported so
// a parent rendering the Download/Revenue pair together can decide up front
// whether the pair needs a shared Unlock, without duplicating this check.
export const estimationIsLocked = estimation => {
  if (!estimation) return false
  if (isBlurredField(estimation)) return true
  const range = isBlurredField(estimation.estimated_range) ? null : estimation.estimated_range
  return !!(
    isBlurredField(estimation.estimated_range) ||
    (range && (isBlurredField(range.min) || isBlurredField(range.max))) ||
    isBlurredField(estimation.ai_downloads) ||
    isBlurredField(estimation.ai_revenue) ||
    isBlurredField(estimation.confidence_score) ||
    isBlurredField(estimation.why_this_estimate) ||
    isBlurredField(estimation.confidence_label)
  )
}

// type: "downloads" | "revenue". estimation: the ai_download_estimation /
// ai_revenue_estimation object from game_info. showUnlock (default true):
// pass false when this card is rendered alongside a sibling estimate card
// and the PARENT already shows one shared Unlock for the pair — this card
// then only shows its own locked/blurred fields, no button of its own.
// bare (default false): render as a plain column (no own ReportCard shell)
// for when a parent has already put Download + Revenue inside ONE shared
// card, side by side, and just needs each half's content.
export default function AIEstimateCard({ type, estimation, showUnlock = true, bare = false }) {
  if (!estimation) return null
  const Wrapper = bare ? "div" : ReportCard

  // Whether the backend masked this whole estimation object in one shot, or
  // just individual fields inside it, must render identically — Download
  // and Revenue sitting side by side previously showed two different looks
  // (a boxed "X is locked" panel vs. the faded inline preview below) purely
  // because of which shape the backend happened to send, not by design.
  // Falling through to the same anyLocked branch below for both cases keeps
  // every field read (estimation.foo) safe: a fully-masked estimation is
  // just {blurred:true,...} with no other keys, so every field access below
  // is simply undefined rather than throwing.
  const isRevenue = type === "revenue"
  const fmt = isRevenue ? formatRevenue : formatDownloads
  const raw = isRevenue ? estimation.ai_revenue : estimation.ai_downloads
  const range = isBlurredField(estimation.estimated_range) ? null : estimation.estimated_range
  const rangeBlurred = isBlurredField(estimation.estimated_range) ? estimation.estimated_range : null
  // Either bound can be locked on its own even when the range object itself
  // isn't — treat that the same as the whole range being locked (one
  // placeholder, not two side-by-side "value - value" chips).
  const rangeBoundBlurred = range && (isBlurredField(range.min) ? range.min : isBlurredField(range.max) ? range.max : null)

  // This card is one section — however many of its fields are individually
  // locked, it shows at most ONE Unlock at the bottom (only when showUnlock
  // is true — a caller rendering a paired grid of these sets it false and
  // shows its own single shared CTA instead); every locked field above
  // renders as plain blurred text (interactive={false}) regardless, so
  // there's never a second button hiding inside the card either way.
  const anyLocked = estimationIsLocked(estimation)

  const display = (rangeBlurred || rangeBoundBlurred)
    ? blurAware(rangeBlurred || rangeBoundBlurred, undefined, "stat", false)
    : range
      ? range.min === 0 && range.max === 0
        ? fmt(0)
        : `${fmt(range.min)} - ${fmt(range.max)}`
      : raw != null
        ? blurAware(raw, fmt, "stat", false)
        : "N/A"

  const score = typeof estimation.confidence_score === "number" ? estimation.confidence_score : null
  const color = score != null ? confidenceColor(score) : null
  const Icon = isRevenue ? DollarSign : Download

  return (
    <Wrapper>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={14} color={REPORT_COLORS.accent} />
          <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
            AI {isRevenue ? "Revenue" : "Download"} Estimate
          </span>
        </div>
        {estimation.confidence_label && (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {badgeAware(estimation.confidence_label, v => <ReportBadge tone="neutral">{v}</ReportBadge>, undefined, false)} confidence
          </span>
        )}
      </div>

      {anyLocked ? (
        // Locked: no partial real fields, no confidence-score bar — just a
        // faded preview of the shape of this card (value + reasoning line),
        // same fade-into-CTA treatment as Recent Reviews, so the whole body
        // reads as ONE gated block instead of a mix of real/blurred pieces.
        <BlurredContentFade
          items={[0]}
          renderItem={() => (
            // BlurredContentFade's own per-item blur is a light progressive
            // fade meant for a multi-row stack (index 0 barely blurs at
            // all) — with a single decorative block here that left the
            // placeholder number fully readable, so it gets its own
            // deliberately heavy blur on top of that.
            <div style={{ filter: "blur(7px)", opacity: 0.6 }}>
              <div style={{ fontSize: "clamp(17px, 5.5vw, 22px)", fontWeight: 700, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", marginBottom: 8 }}>
                {fmt(isRevenue ? 48213 : 128430)}
              </div>
              <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary }}>{PLACEHOLDER_SENTENCE}</div>
            </div>
          )}
          maxHeight={90}
          hideCta={!showUnlock}
        />
      ) : (
        <>
          <div style={{ fontSize: "clamp(17px, 5.5vw, 22px)", fontWeight: 700, color: REPORT_COLORS.textPrimary, fontFamily: "monospace", marginBottom: score != null ? 10 : 8, overflowWrap: "anywhere" }}>
            {display}
          </div>

          {score != null && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: REPORT_COLORS.textMuted }}>Confidence Score</span>
                <span style={{ fontSize: 9, fontWeight: 600, color }}>{score}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, backgroundColor: REPORT_COLORS.cardBorder, overflow: "hidden" }}>
                <div style={{ width: `${score}%`, height: "100%", backgroundColor: color, borderRadius: 2 }} />
              </div>
            </div>
          )}

          {estimation.why_this_estimate && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary }}>{blurAware(estimation.why_this_estimate, formatNumbersInText, "lg", false)}</div>}
        </>
      )}
    </Wrapper>
  )
}
